// App router — react-router v7 (Remix-style createBrowserRouter).
// Source : PRD 09 §3 routing.
//
// Pattern : routes lazy-loaded pour minimiser le bundle initial. Chaque feature
// (inbox, settings, onboarding, auth) est chargée à la navigation.
//
// Auth guards : le layout /app/* vérifie la session via AuthContext. Si absente,
// redirect /auth/signin. Pattern inverse : /auth/* redirect / si déjà connecté.
import { createBrowserRouter, Navigate, redirect } from "react-router";
import { lazy, Suspense } from "react";
import { PageSkeleton } from "@/components/common/PageSkeleton";
import { AuthGuard } from "@/components/common/AuthGuard";
import { INBOX_FOLDER_PATHS } from "@/features/inbox/folder-from-path";

// Lazy routes — chaque chunk séparé, loaded on demand
const DashboardLayout = lazy(() =>
  import("@/components/layout/DashboardLayout").then((m) => ({ default: m.DashboardLayout })),
);
const AuthLayout = lazy(() =>
  import("@/components/layout/AuthLayout").then((m) => ({ default: m.AuthLayout })),
);
const OnboardingLayout = lazy(() =>
  import("@/routes/onboarding/_layout").then((m) => ({ default: m.OnboardingLayout })),
);

const InboxPage = lazy(() =>
  import("@/features/inbox/layout/InboxLayout").then((m) => ({ default: m.InboxLayout })),
);
const CRMPage = lazy(() =>
  import("@/features/crm/CRMPage").then((m) => ({ default: m.CRMPage })),
);
const ContactsPage = lazy(() => import("@/routes/contacts"));
const CompaniesPage = lazy(() => import("@/routes/companies"));
const TasksPage = lazy(() =>
  import("@/features/tasks/TasksPage").then((m) => ({ default: m.TasksPage })),
);
const ProjectsPage = lazy(() =>
  import("@/features/tasks/ProjectsPage").then((m) => ({ default: m.ProjectsPage })),
);
const NotesPage = lazy(() =>
  import("@/features/notes/NotesPage").then((m) => ({ default: m.NotesPage })),
);
const DocumentsPage = lazy(() =>
  import("@/features/documents/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
);
const PublicNotePage = lazy(() =>
  import("@/routes/public-note").then((m) => ({ default: m.PublicNotePage })),
);
// Settings — layout + 8 lazy sub-routes (PRD 11bis §2.1). Sections stay
// lazy to keep the main bundle slim (~+27 KB if eager-imported). Per
// react-guard PR #33 audit, the only cost is a brief PageSkeleton flash
// on first navigation to each section — accepted as the better trade-off.
const SettingsLayout = lazy(() => import("@/routes/settings/_layout"));
const SettingsProfile = lazy(() => import("@/routes/settings/sections/profile"));
const SettingsPreferences = lazy(() => import("@/routes/settings/sections/preferences"));
const SettingsAccounts = lazy(() => import("@/routes/settings/sections/accounts"));
const SettingsAi = lazy(() => import("@/routes/settings/sections/ai"));
const SettingsSecurity = lazy(() => import("@/routes/settings/sections/security"));
const SettingsNotifications = lazy(() => import("@/routes/settings/sections/notifications"));
const SettingsLegal = lazy(() => import("@/routes/settings/sections/legal"));
const SettingsDanger = lazy(() => import("@/routes/settings/sections/danger"));

// Phase 11bis : pages légales publiques (privacy + terms).
const LegalLayout = lazy(() =>
  import("@/routes/legal/LegalLayout").then((m) => ({ default: m.LegalLayout })),
);
const PrivacyPage = lazy(() => import("@/routes/legal/PrivacyPage"));
const TermsPage = lazy(() => import("@/routes/legal/TermsPage"));

const SignInPage = lazy(() => import("@/routes/auth/signin"));
const SignUpPage = lazy(() => import("@/routes/auth/signup"));
const ResetPage = lazy(() => import("@/routes/auth/reset"));
const VerifiedPage = lazy(() => import("@/routes/auth/verified"));
const CallbackPage = lazy(() => import("@/routes/auth/callback"));
const AurinkoCallbackPage = lazy(() => import("@/routes/auth/aurinko/callback"));

const OnboardingStep = lazy(() => import("@/routes/onboarding/[step]"));

function withSuspense(Element: React.ComponentType) {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Element />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Landing / app root
  {
    path: "/",
    element: (
      <AuthGuard>
        <Suspense fallback={<PageSkeleton />}>
          <DashboardLayout />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      { index: true, element: withSuspense(InboxPage) },
      ...INBOX_FOLDER_PATHS.map((path) => ({
        path,
        element: withSuspense(InboxPage),
      })),
      { path: "label/*", element: withSuspense(InboxPage) },
      { path: "crm", element: withSuspense(CRMPage) },
      { path: "crm/:section", element: withSuspense(CRMPage) },
      { path: "contacts", element: withSuspense(ContactsPage) },
      { path: "companies", element: withSuspense(CompaniesPage) },
      { path: "tasks", element: withSuspense(TasksPage) },
      { path: "projects", element: withSuspense(ProjectsPage) },
      { path: "notes", element: withSuspense(NotesPage) },
      { path: "documents", element: withSuspense(DocumentsPage) },
      {
        path: "settings",
        element: withSuspense(SettingsLayout),
        children: [
          // Loader-redirect (no component → no Suspense roundtrip) — same
          // pattern as /legal and /auth indexes. Simplify M1.
          { index: true, loader: () => redirect("/settings/profile") },
          { path: "profile", element: withSuspense(SettingsProfile) },
          { path: "preferences", element: withSuspense(SettingsPreferences) },
          { path: "accounts", element: withSuspense(SettingsAccounts) },
          { path: "ai", element: withSuspense(SettingsAi) },
          { path: "security", element: withSuspense(SettingsSecurity) },
          { path: "notifications", element: withSuspense(SettingsNotifications) },
          { path: "legal", element: withSuspense(SettingsLegal) },
          { path: "danger", element: withSuspense(SettingsDanger) },
        ],
      },
    ],
  },

  // Public note view (NO auth) — slug-based read-only access.
  { path: "/n/:slug", element: withSuspense(PublicNotePage) },

  // Pages légales publiques (no auth)
  {
    path: "/legal",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <LegalLayout />
      </Suspense>
    ),
    children: [
      { index: true, loader: () => redirect("/legal/privacy") },
      { path: "privacy", element: withSuspense(PrivacyPage) },
      { path: "terms", element: withSuspense(TermsPage) },
    ],
  },

  // Auth routes (public)
  {
    path: "/auth",
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <AuthLayout />
      </Suspense>
    ),
    children: [
      { index: true, loader: () => redirect("/auth/signin") },
      { path: "signin", element: withSuspense(SignInPage) },
      { path: "signup", element: withSuspense(SignUpPage) },
      { path: "reset", element: withSuspense(ResetPage) },
      { path: "verified", element: withSuspense(VerifiedPage) },
      { path: "callback", element: withSuspense(CallbackPage) },
      { path: "aurinko/callback", element: withSuspense(AurinkoCallbackPage) },
    ],
  },

  // Onboarding (require auth, step in URL)
  {
    path: "/onboarding",
    element: (
      <AuthGuard>
        <Suspense fallback={<PageSkeleton />}>
          <OnboardingLayout />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      { index: true, loader: () => redirect("/onboarding/1") },
      { path: ":step", element: withSuspense(OnboardingStep) },
    ],
  },

  // Catch-all 404
  { path: "*", element: <Navigate to="/" replace /> },
]);
